// @ts-check
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Flex, Icon, Loading, P, Small } from "orcs-design-system";
import { StyledToolCallBubble } from "./StyledComponents";
import { toolCallMessage } from "./prop-types";

/**
 * @typedef {Object} ToolMessageProps
 * @property {import("./types").AIToolCall} message
 * @property {import("./types").AIToolCall[]} [allToolsCalled]
 *
 */

/**
 * @typedef {Object} ToolCallLabelProps
 * @property {import("./types").AIToolCall} message
 */

const isSubAgent = (name) => {
  return name?.toLowerCase().endsWith("assistant");
};

/**
 * @param {ToolCallLabelProps} props
 * @returns {React.ReactElement}
 */
const ToolCallLabel = ({ message }) => {
  const { status, args, error = null } = message;
  const isRunning = status === "pending";
  const isCompleted = status === "done";
  const name = args?.name;
  const _isSubAgent = isSubAgent(name);

  const color = "greyDarker";

  const text = useMemo(() => {
    if (_isSubAgent) {
      return "Delegated to assistant";
    }

    if (isRunning) {
      return "Running...";
    }

    if (error) {
      return "Failed";
    }

    if (isCompleted) {
      return "Completed";
    }

    return "Ready";
  }, [isRunning, isCompleted, error, _isSubAgent]);

  return (
    <Flex flex="1" width="100%">
      <P color={color}>{name}</P>

      <Small ml="auto" color={color} fontWeight="600">
        {text}
      </Small>
    </Flex>
  );
};

ToolCallLabel.propTypes = {
  message: toolCallMessage,
};

/**
 * @param {ToolMessageProps} props
 * @returns {React.ReactElement}
 */
export const ToolMessage = ({ message, allToolsCalled = [] }) => {
  const { status } = message;
  const isRunning = status === "pending";

  return (
    <StyledToolCallBubble>
      {/* @ts-ignore -- small is not a prop of Loading */}
      <Box mr="s">
        {isRunning && !isSubAgent(message.args?.name) ? (
          // @ts-ignore -- small is not a prop of Loading
          <Loading small mr="s" />
        ) : (
          // @ts-ignore -- icon is not a prop of Icon
          <Icon icon={["fas", "check-circle"]} />
        )}
      </Box>

      <Small color="grey">{`${allToolsCalled.length} ${
        allToolsCalled.length > 1 ? "tools" : "tool"
      } used`}</Small>
    </StyledToolCallBubble>
  );
};

ToolMessage.propTypes = {
  message: toolCallMessage,
  allToolsCalled: PropTypes.arrayOf(toolCallMessage),
};
